import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material'
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Assessment,
  Warning,
  CheckCircle,
  Schedule,
  Assignment
} from '@mui/icons-material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts'
import { useSnackbar } from 'notistack'
import api from '../services/api'

export const DeficiencyStatisticsPage = () => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({})
  const [chartData, setChartData] = useState({
    byType: [],
    bySeverity: [],
    byDepartment: [],
    trends: []
  })

  useEffect(() => {
    loadDeficiencyStatistics()
  }, [])

  const loadDeficiencyStatistics = async () => {
    setLoading(true)
    try {
      const response = await api.get('/mrd/dashboard/def-stats')
      
      if (response.data.success) {
        const data = response.data.data
        setStats({
          totalDeficiencies: data.summary?.total || 0,
          resolvedDeficiencies: data.summary?.resolved || 0,
          pendingDeficiencies: data.summary?.open || 0,
          overdueDeficiencies: data.summary?.overdue || 0,
          resolutionRate: data.summary?.resolutionRate || 0,
          avgResolutionTime: data.resolutionMetrics?.averageResolutionTime || 0,
          qualityScore: Math.max(0, 100 - (data.summary?.overdue || 0) * 2), // Simple quality score calculation
          overdueRate: data.summary?.total > 0 ? ((data.summary?.overdue || 0) / data.summary.total * 100).toFixed(2) : 0,
          topDeficiencyType: data.topDeficiencyTypes?.[0]?.type || 'N/A',
          topDepartment: data.departmentBreakdown?.[0]?.department || 'N/A'
        })
        
        // Transform data for charts
        const chartData = {
          byType: data.topDeficiencyTypes?.slice(0, 10).map(item => ({
            type: item.type,
            count: item.count
          })) || [],
          bySeverity: Object.entries(data.severityDistribution || {}).map(([severity, count]) => ({
            name: severity,
            count: count
          })),
          byDepartment: data.departmentBreakdown?.slice(0, 10).map(item => ({
            department: item.department,
            count: item.total
          })) || [],
          trends: [] // Would need historical data from backend
        }
        setChartData(chartData)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load deficiency statistics', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Deficiency Statistics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive analytics and trends for deficiency management
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDeficiencyStatistics}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="primary" fontWeight={600}>
                    {stats.totalDeficiencies || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Deficiencies
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="success.main" fontWeight={600}>
                    {stats.resolvedDeficiencies || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Resolved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="warning.main" fontWeight={600}>
                    {stats.pendingDeficiencies || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Warning color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="error" fontWeight={600}>
                    {stats.overdueDeficiencies || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Resolution Rate</Typography>
              </Box>
              <Typography variant="h3" color="success.main" fontWeight={600}>
                {stats.resolutionRate || 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average time to resolve: {stats.avgResolutionTime || 0} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Quality Score</Typography>
              </Box>
              <Typography variant="h3" color="info.main" fontWeight={600}>
                {stats.qualityScore || 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Based on deficiency frequency and severity
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingDown color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Overdue Rate</Typography>
              </Box>
              <Typography variant="h3" color="warning.main" fontWeight={600}>
                {stats.overdueRate || 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Files with overdue deficiencies
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Deficiencies by Type */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Deficiencies by Type
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.byType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Deficiencies by Severity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Deficiencies by Severity
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.bySeverity}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {chartData.bySeverity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Deficiencies by Department */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Deficiencies by Department
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.byDepartment} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="department" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Trends */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="created" stroke="#8884d8" name="Created" />
                <Line type="monotone" dataKey="resolved" stroke="#82ca9d" name="Resolved" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Summary */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary & Insights
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {stats.totalDeficiencies === 0 ? (
          <Alert severity="info">
            No deficiency data available for analysis.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Key Findings:
              </Typography>
              <Typography variant="body2" paragraph>
                • Most common deficiency type: {stats.topDeficiencyType || 'N/A'}
              </Typography>
              <Typography variant="body2" paragraph>
                • Department with highest deficiencies: {stats.topDepartment || 'N/A'}
              </Typography>
              <Typography variant="body2" paragraph>
                • Average resolution time: {stats.avgResolutionTime || 0} days
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Recommendations:
              </Typography>
              <Typography variant="body2" paragraph>
                • Focus on {stats.topDeficiencyType || 'common'} deficiency prevention
              </Typography>
              <Typography variant="body2" paragraph>
                • Provide additional training to {stats.topDepartment || 'departments'} with high deficiency rates
              </Typography>
              <Typography variant="body2" paragraph>
                • Implement automated reminders for overdue deficiencies
              </Typography>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  )
}

